// Copyright (c) Microsoft. All rights reserved.

import { HostContext } from '../../embed-script/HostContext';
import { AgentOverride, AgentOverrides } from '../models/AgentConfig';
import { IChatMessage, UserFeedback, UserFeedbackSource } from '../models/ChatMessage';
import { ResponseStyle } from '../models/UserPreferences';
import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { ServiceInfo } from '../models/ServiceInfo';
import { IAsk } from '../semantic-kernel/model/Ask';
import { IAskResult } from '../semantic-kernel/model/AskResult';
import { BackendServiceUrl, BaseService } from './BaseService';
import { ChatHistoryDto } from '../../api/models';
import { AiProviderSelection } from '../models/AiProvider';
import { ChatExportResult, ChatHistoryExportPayload } from '../models/ChatExport';

// When the export is rate-limited, resolve when the user may try again: prefer the gate's explicit
// nextEligibleAt (409 body), else derive it from a Retry-After header (the burst limiter's 429),
// else leave undefined so the caller shows a date-less message.
const parseNextEligibleAt = (data: unknown, headers: Headers): string | undefined => {
    if (data && typeof data === 'object' && 'nextEligibleAt' in data) {
        const value = (data as { nextEligibleAt?: unknown }).nextEligibleAt;
        if (typeof value === 'string') {
            return value;
        }
    }

    const retryAfter = headers.get('Retry-After');
    if (retryAfter) {
        const seconds = parseInt(retryAfter, 10);
        if (!Number.isNaN(seconds)) {
            return new Date(Date.now() + seconds * 1000).toISOString();
        }
    }

    return undefined;
};

// The over-cap 400 body is { reason: 'too_many', maxChats }. Pull the server's authoritative cap so
// the UI can word the message with the real number; undefined if absent (caller falls back to the
// client-side constant).
const parseMaxChats = (data: unknown): number | undefined => {
    if (data && typeof data === 'object' && 'maxChats' in data) {
        const value = (data as { maxChats?: unknown }).maxChats;
        if (typeof value === 'number') {
            return value;
        }
    }

    return undefined;
};

export class ChatService extends BaseService {
    constructor(accessToken: string, baseServiceUrl: string = BackendServiceUrl) {
        super(accessToken, baseServiceUrl);
    }

    /**
     * Server-side chat-history export (#2134): POST the selected chat ids; the backend enforces the
     * 14-day cooldown, redacts PII, and returns the finished file. Uses the raw header/status helper
     * (not getResponseAsync) so the 409 gate and 429 burst limiter can be surfaced to the user
     * instead of being swallowed into a global alert.
     */
    public exportChatHistoryAsync = async (chatIds: string[], hostContext?: HostContext): Promise<ChatExportResult> => {
        const { status, headers, data } = await this.getResponseWithHeadersAsync({
            commandPath: 'chats/export',
            method: 'POST',
            body: { chatIds },
            hostContext,
        });

        if (status === 200) {
            return {
                status: 'success',
                payload: (data as ChatHistoryExportPayload | undefined) ?? { export: {}, skippedChatCount: 0 },
            };
        }
        if (status === 409 || status === 429) {
            return { status: 'rate_limited', nextEligibleAt: parseNextEligibleAt(data, headers) };
        }
        if (status === 400) {
            // The only 400 reachable from the UI is the over-cap guard (empty selection is blocked
            // client-side; an oversized body is a 413). Map it to `too_many` so the user gets a
            // specific message instead of the generic error, carrying the server's cap when present.
            return { status: 'too_many', maxChats: parseMaxChats(data) };
        }
        if (status === 404) {
            return { status: 'disabled' };
        }
        return { status: 'error' };
    };

    public createChatAsync = async (hostContext?: HostContext): Promise<ICreateChatSessionResponse> => {
        const result = await this.getResponseAsync<ICreateChatSessionResponse>({
            commandPath: 'chats',
            method: 'POST',
            hostContext,
        });

        return result;
    };

    public getChatAsync = async (chatId: string, hostContext: HostContext): Promise<IChatSession> => {
        const result = await this.getResponseAsync<IChatSession>({
            commandPath: `chats/${chatId}`,
            method: 'GET',
            hostContext,
        });

        return result;
    };

    public getAllChatsAsync = async (hostContext: HostContext): Promise<IChatSession[]> => {
        const result = await this.getResponseAsync<IChatSession[]>({
            commandPath: 'chats',
            method: 'GET',
            hostContext,
        });
        return result;
    };

    public getLastChatSessionAsync = async (hostContext?: HostContext): Promise<IChatSession | null> => {
        const result = await this.getResponseAsync<IChatSession | null>({
            commandPath: `chatSession/lastChatSession`,
            method: 'GET',
            hostContext,
        });
        return result;
    };

    // new async funtion to update chatmessage userfeedback calling endpoint chatSession/updateUserFeedback/{chatId:guid}/{messageId:guid}/{feedback}
    // The optional `source` (thumbs vs header) rides along as a query param so the two rating
    // surfaces can be distinguished in analytics; omitted clients leave it null server-side.
    public updateUserFeedbackAsync = async (
        chatId: string,
        messageId: string,
        feedback: UserFeedback,
        hostContext?: HostContext,
        source?: UserFeedbackSource,
    ): Promise<void> => {
        const query = source ? `?source=${encodeURIComponent(source)}` : '';
        await this.getResponseAsync({
            commandPath: `chatSession/updateUserFeedback/${chatId}/${messageId}/${feedback.toString()}${query}`,
            method: 'POST',
            hostContext,
        });
    };

    /**
     * Restyle (#1551): ask the server to rewrite an existing bot message in the opposite
     * verbosity. The new content is delivered via SignalR (ReceiveMessageUpdate); the HTTP
     * response just confirms termination. Surfaces as ConflictError on 409 (sources gone).
     */
    public restyleMessageAsync = async (
        chatId: string,
        messageId: string,
        targetStyle: ResponseStyle,
        hostContext?: HostContext,
    ): Promise<void> => {
        await this.getResponseAsync({
            commandPath: `chats/${chatId}/messages/${messageId}/restyle`,
            method: 'POST',
            body: { targetStyle },
            hostContext,
        });
    };

    public deleteChatSessionAsync = async (chatId: string, hostContext?: HostContext): Promise<void> => {
        await this.getResponseAsync({
            commandPath: `deleteChatSession/${chatId}`,
            method: 'DELETE',
            hostContext,
        });
    };

    public getChatMessagesAsync = async (
        chatId: string,
        startIdx: number,
        count: number,
        hostContext?: HostContext,
    ): Promise<IChatMessage[]> => {
        const result = await this.getResponseAsync<IChatMessage[]>({
            commandPath: `chats/${chatId}/messages?startIdx=${startIdx.toString()}&count=${count.toString()}`,
            method: 'GET',
            hostContext,
        });

        // Messages are returned with most recent message at index 0 and oldest message at the last index,
        // so we need to reverse the order for render
        return result.reverse();
    };

    public getBotResponseAsync = async (
        ask: IAsk,
        hostContext?: HostContext,
        providerSelection?: AiProviderSelection,
        agentOverrides?: AgentOverrides,
    ): Promise<IAskResult> => {
        const chatId = ask.variables?.find((variable) => variable.key === 'chatId')?.value as string;

        // Both the per-agent overrides and the global default provider/model travel in the request body so
        // the server validates them through the same effective-pair + availability gate. The global
        // selection used to ride a `?provider=&modelId=` query string that no webapi code read — a no-op
        // that also bypassed that gate (#2436).
        const hasAgentOverrides = agentOverrides != null && Object.keys(agentOverrides).length > 0;
        const defaultAgentOverride: AgentOverride | undefined =
            providerSelection && (providerSelection.providerId || providerSelection.modelId)
                ? { provider: providerSelection.providerId, model: providerSelection.modelId }
                : undefined;

        const requestBody = {
            ...ask,
            ...(hasAgentOverrides ? { agentOverrides } : {}),
            ...(defaultAgentOverride ? { defaultAgentOverride } : {}),
        };

        const result = await this.getResponseAsync<IAskResult>({
            commandPath: `chats/${chatId}/messages`,
            method: 'POST',
            body: requestBody,
            hostContext,
        });

        return result;
    };

    public cancelChatAsync = async (chatId: string, hostContext?: HostContext): Promise<void> => {
        try {
            await this.getResponseAsync({
                commandPath: `chats/${chatId}/cancel`,
                method: 'POST',
                hostContext,
            });
        } catch {
            // Idempotent — if the backend finished between our click and this call we don't
            // surface a user-facing error; the UI is already returned to edit mode.
        }
    };

    public getServiceInfoAsync = async (hostContext?: HostContext): Promise<ServiceInfo> => {
        const result = await this.getResponseAsync<ServiceInfo>({
            commandPath: `info`,
            method: 'GET',
            hostContext,
        });

        return result;
    };

    public async submitCsatAsync(
        chatId: string,
        satisfaction: number,
        comment?: string,
        hostContext?: HostContext,
        source?: 'modal' | 'inline',
    ): Promise<void> {
        await this.getResponseAsync({
            commandPath: 'chat/csat/submit',
            method: 'POST',
            body: { chatId, satisfaction, comment, source },
            hostContext,
        });
    }

    public async declineCsatAsync(chatId?: string, hostContext?: HostContext): Promise<void> {
        // The inline widget (#1641) passes the chat id so the server can persist the "responded"
        // flag on the flagged message; the legacy modal omits it. Server treats absence as a no-op
        // for the per-message flag and still records the user-level cooldown.
        const commandPath = chatId ? `chat/csat/decline?chatId=${encodeURIComponent(chatId)}` : 'chat/csat/decline';
        await this.getResponseAsync({
            commandPath,
            method: 'POST',
            hostContext,
        });
    }

    public async shouldShowCsatAsync(hostContext?: HostContext, chatId?: string): Promise<boolean> {
        // The chat being closed lets the server skip the modal when that chat already offered the
        // inline widget (#2638) — it can be an older chat than the last session.
        const result = await this.getResponseAsync<boolean>({
            commandPath: chatId ? `chat/csat/status?chatId=${encodeURIComponent(chatId)}` : 'chat/csat/status',
            method: 'GET',
            hostContext,
        });
        return result;
    }

    public getChatHistoryAsync = async (hostContext?: HostContext): Promise<ChatHistoryDto[]> => {
        const result = await this.getResponseAsync<ChatHistoryDto[]>({
            commandPath: 'chatSession/history',
            method: 'GET',
            hostContext,
        });
        return result;
    };
}
